import React from 'react';
import AutoComplete, { createFilterOptions } from '@mui/material/Autocomplete';
import TextField from '../TextField/TextField';
import Checkbox from '@mui/material/Checkbox';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { alpha, lighten, styled } from '@mui/system';
import AddIcon from '@mui/icons-material/Add';

const baseFilter = createFilterOptions();

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const GroupHeader = styled('div')(({ theme }) => ({
    position: 'sticky',
    top: '-8px',
    padding: '4px 10px',
    fontWeight: 600,
    color: theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.primary.main,
    backgroundColor: theme.palette.mode === 'dark'
      ? alpha(theme.palette.primary.main, 0.25)
      : lighten(theme.palette.primary.light, 0.85)
  }));
  
  const GroupItems = styled('ul')({
    padding: 0,
  });

export default function AutoCompleteField(props) {
    const label = props != null ? props.label : null;
    const onChange = props != null ? props.onChange : null;
    const error = props != null ? props.error : null;
    const disabled = props != null ? props.disabled : null;
    const style = props?.style ?? props?.sx ?? {};
    const require = props != null ? props["required"]  : null;
    const requireComplete = props != null ? props["requiredComplete"] : null;
    const dataCy = props['data-cy'];
    const multiline = props != null ? props.multiline : null;
    const renderOption = props != null ? props.renderOption : null;
    const placeholder = props != null ? props.placeholder : null;
    const minRows = props != null ? props.minRows : null;
    const value = props != null ? props.value : null;
    const options = props != null ? props.options : [];
    const helperText = props != null ? props.helperText : null;
    const multiple = props != null ? props.multiple : false;
    const fullWidth = props?.fullWidth ?? true;
    const stayOpen = props != null ? props.stayOpen : null;
    const size = props != null ? props.size : undefined;
    const disableClearable = props != null ? props.disableClearable : false;
    const slotProps = props != null ? props.slotProps : undefined;
    const groupBy = props != null ? props.groupBy : null;
    const noneMessage = props != null ? props.noneMessage : null;
    const createOption = props != null ? props.createOption : null;
    const chipInput = props != null ? props.chipInput : null;
    let validOptions = [];
    if(options != null && options.length > 0){
        for(let row of options){
            if(row.hide !== true){
                validOptions.push(row);
            }
        }
    }

    const handleChange = (e, newValue, reason) => {
        if (createOption) {
            if (Array.isArray(newValue)) {
                const createIdx = newValue.findIndex(v => v && typeof v === 'object' && v.isCreate);
                if (createIdx >= 0) {
                    const item = newValue[createIdx];
                    const rest = newValue.filter((_, i) => i !== createIdx);
                    createOption(item.inputValue, rest);
                    return;
                }
                const strIdx = newValue.findIndex(v => typeof v === 'string');
                if (strIdx >= 0) {
                    const str = newValue[strIdx];
                    const rest = newValue.filter((_, i) => i !== strIdx);
                    createOption(str, rest);
                    return;
                }
            } else {
                if (reason === 'createOption' && typeof newValue === 'string') {
                    createOption(newValue);
                    return;
                }
                if (newValue && typeof newValue === 'object' && newValue.isCreate) {
                    createOption(newValue.inputValue);
                    return;
                }
            }
        }
        onChange(e, newValue);
    };

    const chipFilterOptions = (opts, state) => {
        const selectedIds = new Set(
            (Array.isArray(value) ? value : [])
                .map(v => v && v.value != null ? String(v.value) : null)
                .filter(Boolean)
        );
        let filtered = baseFilter(opts.filter(o => !selectedIds.has(String(o.value))), state);
        if (createOption && state.inputValue) {
            const typed = state.inputValue.trim().toLowerCase();
            const exists = opts.some(o => (o.label || '').trim().toLowerCase() === typed);
            if (typed && !exists) {
                filtered.push({
                    isCreate: true,
                    inputValue: state.inputValue.trim(),
                    label: `Create "${state.inputValue.trim()}"`,
                    value: `__create_${state.inputValue}__`
                });
            }
        }
        return filtered;
    };

    const defaultRenderOption = (props, option) => {
        const { key, ...otherProps } = props;
        const uniqueKey = option.value || key;
        if (option.isCreate) {
            return (
                <li key={uniqueKey} {...otherProps}>
                    <AddIcon sx={{ mr: 1 }} />
                    Create "{option.inputValue}"
                </li>
            );
        }
        return <li key={uniqueKey} {...otherProps}>{option.label}</li>;
    };
    
    if (chipInput) {
        return (
            <AutoComplete
                data-cy={dataCy}
                multiple
                size={size}
                disableCloseOnSelect
                disableClearable={disableClearable}
                value={Array.isArray(value) ? value : []}
                options={validOptions || []}
                sx={style}
                getOptionLabel={(option) => typeof option === 'string' ? option : (option?.label || '')}
                isOptionEqualToValue={(option, val) => option.value === val.value}
                filterOptions={chipFilterOptions}
                renderOption={renderOption || defaultRenderOption}
                onChange={handleChange}
                freeSolo={!!createOption}
                forcePopupIcon
                slotProps={slotProps}
                renderInput={(params) => <TextField {...params} helperText={helperText}
                    error={error} label={label}
                    required={require}
                    requiredComplete={requireComplete}
                    placeholder={placeholder}
                    fullWidth={fullWidth}
                />}
                disabled={disabled}
                fullWidth={fullWidth}
                required={require}
            />
        );
    }

    return !stayOpen ?
            <AutoComplete
                data-cy={dataCy}
                multiple={multiple}
                size={size}
                defaultValue={multiple === true || multiple === undefined ? [] : null}
                value={value != null && value !== '' ? value :
                       multiple === true || multiple === undefined ? [] : null}
                options={validOptions || []}
                sx={style}
                getOptionLabel={(option) => option.label || ''}
                isOptionEqualToValue={(option, value) => option.value === value.value || value.value === '' ||
                    option.value === null || Object.keys(value).length === 0 || (Array.isArray(option) && option.length === 0)}
                onChange={handleChange}
                renderInput={(params) => <TextField {...params} helperText={helperText}
                    error={error} label={label}
                    required={require}
                    requiredComplete={requireComplete}
                    multiline={multiline}
                    minRows={minRows}
                    placeholder={placeholder}
                    fullWidth={fullWidth}
                />}
                getOptionDisabled={(option) => option.disabled == null ? false : option.disabled}
                disabled={disabled}
                fullWidth={fullWidth}
                required={require}
                renderOption={renderOption || defaultRenderOption}
                freeSolo={!!createOption}
                forcePopupIcon
                groupBy={(option) => option[groupBy]}
                renderGroup={groupBy != null ? (params) => (
                    <li key={params.key}>
                      <GroupHeader>{params.group === 'none' ? noneMessage : params.group}</GroupHeader>
                      <GroupItems>{params.children}</GroupItems>
                    </li>
                ) : null}
            /> :
            <AutoComplete
                data-cy={dataCy}
                multiple={multiple}
                size={size}
                disableCloseOnSelect
                disableClearable={disableClearable}
                defaultValue={multiple === true || multiple === undefined ? [] : null}
                value={value != null && value !== '' ? value :
                       multiple === true || multiple === undefined ? [] : null}
                options={validOptions || []}
                sx={style}
                placeholder={placeholder}
                getOptionLabel={(option) => option?.label || ''}
                isOptionEqualToValue={(option, value) => option.value === value.value || value.value === '' ||
                option.value === null || Object.keys(value).length === 0 || (Array.isArray(option) && option.length === 0)}
                renderOption={renderOption || ((props, option, { selected }) => {
                    const { key, ...otherProps } = props;
                    return (
                        <li key={option.value || key} {...otherProps}>
                            <Checkbox
                                icon={icon}
                                checkedIcon={checkedIcon}
                                sx={{ marginRight: 8, color: 'secondary.main' }}
                                checked={selected}
                            />
                            {option.label}
                        </li>
                    );
                })}
                onChange={onChange}
                slotProps={slotProps}
                renderInput={(params) => <TextField {...params} helperText={helperText}
                    error={error} label={label}
                    required={require}
                    requiredComplete={requireComplete}
                    multiline={multiline}
                    minRows={minRows}
                    placeholder={placeholder}
                    fullWidth={fullWidth}
                />}
                disabled={disabled}
                fullWidth={fullWidth}
                required={require}
                freeSolo={!!createOption}
                forcePopupIcon
                groupBy={(option) => option[groupBy]}
                renderGroup={groupBy != null ? (params) => (
                    <li key={params.key}>
                      <GroupHeader>{params.group === 'none' ? noneMessage : params.group}</GroupHeader>
                      <GroupItems>{params.children}</GroupItems>
                    </li>
                ) : null}
            />
}
